import NextApp, { AppContext } from 'next/app';
import { IncomingMessage } from 'http';

import { Stage, stageFromString } from '@eventbrite/webapp-constants';
import { ConsentManagerScripts } from '@eventbrite/webapp-data';
import { getLocaleFromRequest } from '@eventbrite/webapp-locales';

import { Wrapper } from '@/components/_shared/Wrapper';
import { IntlProviderWrapper } from '@/providers/IntlProviders';
import { QueryClientProvider } from '@/providers/QueryClientProvider';
import { ListingsAppProps } from '@/types';
import { getLocaleFromProps } from '@/utils/locale';
import { getDefaultDomainUrl, getDomainUrl } from '@/utils/utils';

import { useAWSRum } from '../../common/monitoring/awsrum';
import { DatadogInit } from '../../common/monitoring/datadogrum';
import { formatLocale } from '../../i18n/locale';

import '@/styles/globals.css';

export default function App({
  Component,
  pageProps,
  requestLocale,
  serverUrl,
  stage,
  rumGuestRoleArn,
  identityPoolId,
  applicationIds,
}: ListingsAppProps) {
  useAWSRum({
    rumGuestRoleArn,
    identityPoolId,
    applicationIds,
  });

  const locale = pageProps.locale;

  const detectedLocale = getLocaleFromProps({ pageProps, requestLocale });
  if (!detectedLocale) {
    console.error(`--locale-- No locale detected for event ${pageProps.context?.basicInfo?.id}`);
  }
  if (!/^[a-z]{2}-[A-Z]{2}$/.test(detectedLocale)) {
    console.error(
      `--locale-- Locale ${detectedLocale} is not in the correct format for event ${pageProps.context?.basicInfo?.id}`,
    );
  }
  if (!detectedLocale.startsWith('en')) {
    console.error(`--locale-- Locale ${detectedLocale} is not english for event ${pageProps.context?.basicInfo?.id}`);
  }

  return (
    <QueryClientProvider>
      <IntlProviderWrapper messages={pageProps.messages} locale={locale}>
        <Wrapper serverUrl={serverUrl} stage={stage} locale={locale}>
          <DatadogInit stage={stage} />
          <ConsentManagerScripts />
          <Component {...pageProps} stage={stage} />
        </Wrapper>
      </IntlProviderWrapper>
    </QueryClientProvider>
  );
}

App.getInitialProps = async (context: AppContext) => {
  const req = context.ctx.req as IncomingMessage;
  const appProps = await NextApp.getInitialProps(context);

  // Extract serverUrl using robust getDomainUrl function
  const serverUrl = getDomainUrl(req) || getDefaultDomainUrl();

  // Get stage from environment variable
  const stage = stageFromString(process.env.STAGE || Stage.TEST);

  // Add RUM configuration - these should come from environment variables or CDK outputs
  const rumGuestRoleArn = process.env.RUM_GUEST_ROLE_ARN || '';
  const identityPoolId = process.env.IDENTITY_POOL_ID || '';
  const applicationIds = process.env.APPLICATION_IDS ? JSON.parse(process.env.APPLICATION_IDS) : {};

  // Fallback locale, we use the event's locale, but in case we can't get it, using the request one instead.
  const requestLocale = formatLocale(getLocaleFromRequest(req));

  return {
    ...appProps,
    requestLocale,
    serverUrl,
    stage,
    rumGuestRoleArn,
    identityPoolId,
    applicationIds,
  };
};
